<script setup>
import { useAbility } from '@casl/vue'
import CommitteeService from '@/services/quality/committee.service'
import { committeeStatusColor, committeeStatusTitle } from '@/enums/quality/committee-status'

// VDataTable (client-side, unlike the VDataTableServer the list page uses) is
// a Vuetify labs component and is not auto-imported — see
// src/pages/app/quality/committee/index.vue for the same requirement. Without
// this the tag renders as an unresolved custom element and the sub-committees
// tab shows nothing.
import { VDataTable } from 'vuetify/lib/labs/VDataTable/index.mjs'
import { VSkeletonLoader } from 'vuetify/lib/labs/components.mjs'
import CommitteeHeader from '@/views/quality/committee/CommitteeHeader.vue'
import CommitteeCreateDrawer from '@/views/quality/committee/CommitteeCreateDrawer.vue'
import CommitteeDissolveDialog from '@/views/quality/committee/CommitteeDissolveDialog.vue'
import CommitteeCharterSection from '@/views/quality/committee/sections/CommitteeCharterSection.vue'
import CommitteeMeetingsSection from '@/views/quality/committee/sections/CommitteeMeetingsSection.vue'
import CommitteeRosterSection from '@/views/quality/committee/sections/CommitteeRosterSection.vue'
import CommitteeObligationSection from '@/views/quality/committee/sections/CommitteeObligationSection.vue'

// unplugin-vue-router derives the route name from the file path
// (`app-quality-committee-id`), not from meta — meta.name is not read by the
// router. Matching src/pages/app/quality/risk/[id].vue, no `name` key is set
// here.
definePage({
  meta: {
    subject: 'committee.menu',
    action: 'committees',
  },
})

const route = useRoute()
const router = useRouter()
const { can } = useAbility()

const committee = ref(null)
const isLoading = ref(false)

// Monotonic token guarding against out-of-order responses — see
// src/pages/app/quality/risk/[id].vue for the same pattern. The detail page
// re-fetches after every drawer save, so an earlier in-flight response must
// not clobber a later one.
let fetchToken = 0

const fetchCommittee = async () => {
  const token = ++fetchToken

  isLoading.value = true
  await CommitteeService.show(route.params.id, data => {
    if (token !== fetchToken) return
    committee.value = data?.data ?? data ?? null
    isLoading.value = false
  }, () => {
    if (token !== fetchToken) return
    isLoading.value = false
  })
}

fetchCommittee()

// Sub-committee navigation pushes to this same route with a different id.
// Vue Router reuses the component instance and does not re-run setup(), so
// without this the URL changes while the previous committee's data stays on
// screen.
watch(() => route.params.id, () => {
  fetchCommittee()
})

const activeTab = ref('charter')
const isEditDrawerVisible = ref(false)
const isDissolveDialogVisible = ref(false)

const subCommitteeHeaders = [
  { title: 'Code', key: 'code', sortable: false },
  { title: 'Name', key: 'name', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
]

const goToSubCommittee = item => {
  router.push({ name: 'app-quality-committee-id', params: { id: item.id } })
}
</script>

<template>
  <section v-if="committee">
    <CommitteeHeader
      :committee="committee"
      @edit="isEditDrawerVisible = true"
      @dissolve="isDissolveDialogVisible = true"
    />

    <VTabs
      v-model="activeTab"
      class="mt-6"
    >
      <VTab value="charter">
        Charter
      </VTab>
      <VTab
        v-if="can('meetings', 'committee.menu')"
        value="meetings"
      >
        Meetings
      </VTab>
      <VTab value="roster">
        Roster
      </VTab>
      <VTab value="obligations">
        Obligations
      </VTab>
      <VTab
        v-if="committee.sub_committees?.length"
        value="sub-committees"
      >
        Sub-committees
      </VTab>
    </VTabs>

    <VWindow
      v-model="activeTab"
      class="mt-6 disable-tab-transition"
    >
      <VWindowItem value="charter">
        <CommitteeCharterSection :committee="committee" />
      </VWindowItem>
      <VWindowItem value="meetings">
        <CommitteeMeetingsSection
          :committee="committee"
          @saved="fetchCommittee"
        />
      </VWindowItem>
      <VWindowItem value="roster">
        <CommitteeRosterSection
          :committee="committee"
          @saved="fetchCommittee"
        />
      </VWindowItem>
      <VWindowItem value="obligations">
        <CommitteeObligationSection
          :committee="committee"
          @saved="fetchCommittee"
        />
      </VWindowItem>
      <VWindowItem value="sub-committees">
        <VDataTable
          :headers="subCommitteeHeaders"
          :items="committee.sub_committees ?? []"
          hover
          @click:row="(_, { item }) => goToSubCommittee(item)"
        >
          <template #item.status="{ item }">
            <VChip
              :color="committeeStatusColor(item.status)"
              size="small"
              label
            >
              {{ committeeStatusTitle(item.status) }}
            </VChip>
          </template>
        </VDataTable>
      </VWindowItem>
    </VWindow>

    <CommitteeCreateDrawer
      v-model:is-drawer-open="isEditDrawerVisible"
      :committee="committee"
      @saved="fetchCommittee"
    />

    <CommitteeDissolveDialog
      v-model:is-drawer-open="isDissolveDialogVisible"
      :committee="committee"
      @saved="fetchCommittee"
    />
  </section>

  <!-- Loading state -->
  <section v-else-if="isLoading">
    <div class="d-flex align-center gap-4 mb-6">
      <VSkeletonLoader
        type="avatar"
        width="48"
      />
      <div>
        <VSkeletonLoader
          type="text"
          width="250"
        />
        <VSkeletonLoader
          type="text"
          width="180"
        />
      </div>
    </div>
    <VSkeletonLoader type="card" />
  </section>

  <!--
    Error state — fetchCommittee's onFail leaves committee null with no
    message on a 403/404. Without this branch the user gets a blank page.
    Same centred empty-state visual pattern as RiskReviewSection.vue.
  -->
  <section v-else>
    <div class="text-center py-16">
      <VIcon
        icon="tabler-alert-triangle"
        size="48"
        class="text-disabled mb-2"
      />
      <p class="text-disabled mb-4">
        This committee could not be found, or you do not have permission to view it.
      </p>
      <VBtn
        variant="tonal"
        prepend-icon="tabler-arrow-left"
        @click="router.push({ name: 'app-quality-committee' })"
      >
        Back to Committees
      </VBtn>
    </div>
  </section>
</template>
